import './cart.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { INewName } from '../../../interfaces/global.interfaces';
import { Data } from '../../../models/data';
import { Page } from '../page';
import { Order } from '../../../models/order';
import { Coupon } from '../../../models/coupons';
import { DataLayerService } from '../../../services/data-layer-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { DataLayerErrorType, Modals, Pages } from '../../../types/enums';
import { Upsell } from '../../shared/Upsell/upsell';
import { DialogCreators } from '../../../utils/dialog-creators';
import { OrderPriceSummary } from '../../shared/OrderPriceSummary/order-price-summary';
import { ProfileService } from '../../../services/profile-service';
import { CartRewards } from './components/cart-rewards/cart-rewards';
import { ICartModules } from './cart.interface';
import { IDataChallenge, IDataChallengeMember, IDataFundraiser } from '../../../models/data.interface';
import { IOrderChallenge, IOrderChallengeEvent } from '../../../models/order.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { MenuItemCartCardController } from '../../shared/MenuItemCartCard/menu-item-cart-card-controller';

/**
 * Represents the shopping cart page, handling cart items, checkout, and promotions.
 */
export class Cart extends Page {
    private readonly _order: Order;
    private readonly _dataLayer: DataLayerService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _profileService: ProfileService;
    private readonly _modules: ICartModules;

    /**
     * Initializes a new instance of the Cart page.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The service managing application data.
     * @param {Order} order - The order instance containing cart items.
     * @param {DataLayerService} dataLayer - The data layer for tracking events.
     * @param {ILoyaltyProvider} loyaltyProvider - The service handling loyalty rewards.
     * @param {ProfileService} profileService - The service handling user profiles.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider, profileService: ProfileService) {
        super("page_cart", eventBus, data, context, localizationService);
        this._order = order;
        this._dataLayer = dataLayer;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._modules = this._initializeModules();
        this._setupEventBusListeners();
        this._setEventListeners();
    }

    /**
     * Initializes and returns instances of cart-related modules.
     * @returns {ICartModules} - An object containing initialized cart modules.
     */
    private _initializeModules(): ICartModules {
        return {
            menuItems: new MenuItemCartCardController({
                parentDivId: "div_cart_items",
                eventBus: this._eventBus,
                data: this._data,
                context: this._context,
                localizationService: this._localizationService,
                order: this._order,
                dataLayerService: this._dataLayer,
                loyaltyProvider: this._loyaltyProvider,
                profileService: this._profileService
            }),
            summary: new OrderPriceSummary(this._eventBus, "div_cart_totals", this._data, this._context, this._localizationService, this._order, this._loyaltyProvider, this._profileService, { isCart: true }),
            upsell: new Upsell(this._eventBus, this._data, this._context, this._localizationService, this._dataLayer),
            cartRewards: new CartRewards(this._eventBus, this._data, this._context, this._localizationService, this._profileService, this._dataLayer, this._order)
        };
    }

    /**
     * Sets up event listeners for cart-related events.
     */
    private _setupEventBusListeners(): void {
        this._eventBus.on(EventTypes.CART_CHANGED, this.initAsync);
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this.initAsync);
        this._eventBus.on(EventTypes.STORE_CHANGED, () => this._renderCouponSection());
        this._eventBus.on(EventTypes.COUPON_INVALID, (error: string) => this._setInvalidCouponError(error));
        this._eventBus.on(EventTypes.DATALAYER_LOADED, () => {
            //initial attempt to capture page_view may not have had the dataLayer loaded yet
            if (this._element?.classList.contains("open")) {
                this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
                this._dataLayer.view_cart(this._order);
            }
        });
        this._eventBus.on(EventTypes.LOGGED_IN, this._onLoggedIn);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._onLoggedOut);
    }

    /**
     * Displays the cart page and updates the header total.
     */
    public override show(): void {
        super.show();

        if (!this._data.storeKey) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS);
            return;
        }

        this._renderPageAsync();
        this._updatePaymentButtonDisplay();

        window.dispatchEvent(new Event("resize"));
        this._order.setSpecialCodeAsync(this._loyaltyProvider);
        this._order.setBoxTopperAsync(this._loyaltyProvider);

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayer) {
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
            this._dataLayer.view_cart(this._order);
        }
    }

    /**
     * Initializes and renders the cart page asynchronously.
     */
    public initAsync = async (): Promise<void> => {
        await this._renderPageAsync();
    }

    /**
     * Renders the cart page, updating items and sections.
     */
    private async _renderPageAsync(): Promise<void> {
        this._hideErrorElements();
        this._updateYourItemsText();
        this._updateLoyaltyPointsText();

        const parentDiv = document.getElementById("div_cart_items");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        const items = this._order.getProperty("Items");
        if (items.length == 0) {
            Util.showElement("div_cart_no_items");
            Util.hideElement("div_cart_items");
            Util.hideElement("btn_cart_add_more_items");
        } else {
            Util.hideElement("div_cart_no_items");
            Util.showElement("div_cart_items");
            Util.showElement("btn_cart_add_more_items");
            this._renderItemsSection();
        }

        this._renderUpsellSection();
        this._renderOrderSummary();
        this._renderCouponSection();
        this._renderFundraiserSection();
        this._renderChallengesButton();
        this._renderChallengesSection();
        this._renderLoyaltySection();
        Util.ensureAnnouncerPresent();

    }

    /**
     * Attaches event listeners to cart-related buttons.
     */
    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_cart_go_to_menu", this._handleAddMoreItems);
        Util.setElement("onclick", "btn_cart_add_more_items_link", this._onAddMoreItemsClick, true);
        Util.setElement("onclick", "btn_cart_add_more_items", this._onAddMoreItemsClick, true);
        Util.setElement("onclick", "btn_cart_continue_to_payment", this._handleContinueToPayment);
        Util.setElement("onclick", "btn_cart_continue_to_payment_mobile", this._handleContinueToPayment);
        Util.setElement("onclick", "btn_promo_code_apply", this._handleApplyPromoCodeAsync);
        Util.setElement("onclick", "btn_cart_fundraiser_apply", this._handleApplyFundraiserByCode);
        Util.setElement("onclick", "btn_cart_fundraiser_selection_remove", this._handleFundraiserRemove);
        Util.setElement("onclick", "btn_cart_challenges_remove", this._handleChallengeRemove);
        Util.setElement("onclick", "a_cart_loyalty_signin_prompt", this._showSignInModal);

        this._setKeyboardEventForLoyaltySignInPrompt();

        const promoInput = document.getElementById("txt_cart_coupon_code");
        if (promoInput)
            promoInput.addEventListener("keydown", (event: KeyboardEvent) => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    this._handleApplyPromoCodeAsync();
                }
            });
    }

    private readonly _onAddMoreItemsClick = (event: MouseEvent): void => {
        this._handleAddMoreItems();

        this._context.setProperty("addMoreItemsClicked", true);
        if (this._dataLayer) {
            const linkText = (event.target as HTMLButtonElement).innerText ?? '';
            this._dataLayer.add_more_items(this._order, linkText, window.location.href);
        }
    }

    /**
     * Handles the event when the user clicks to add more items to the cart.
     */
    private readonly _handleAddMoreItems = (): void => {
        if (this._data.storeKey)
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
        else
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS);
    }

    /**
     * Handles the event when the user proceeds to payment.
     */
    private readonly _handleContinueToPayment = (): void => {
        if (this._order.getProperty("Items").length == 0) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names(
                    "Error_NoItemsHaveBeenAddedToOrder"
                ),
                true
            );
            return;
        }

        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.CHECKOUT);
    }

    /**
     * Updates the visibility of the payment button based on whether the cart contains items.
     */
    private _updatePaymentButtonDisplay(): void {
        if (this._order.getProperty("Items").length > 0) {
            Util.showElement("btn_cart_continue_to_payment");
            Util.showElement("div_cart_continue_to_payment_mobile");
        } else {
            Util.hideElement("btn_cart_continue_to_payment");
            Util.hideElement("div_cart_continue_to_payment_mobile");
        }
    }

    /**
     * Updates the "Your Items" text in the cart based on the number of items.
     */
    private _updateYourItemsText(): void {
        const itemsCount = this._order.getAmountOfItemsInCart();

        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
            const tempText = this._localizationService.names("YourItems", lang).replace("{{number}}", itemsCount.toString());
            return { text: tempText, lc: lang };
        });

        Util.setElement("innerText", "h2_cart_your_items", this._localizationService.names("YourItems").replace("{{number}}", itemsCount.toString()));
        Util.setElement("ltagj", "h2_cart_your_items", Util.toLtagj(nameObj));
    }

    /**
     * Renders the cart items section.
     */
    private _renderItemsSection(): void {
        if (this._modules.menuItems.ui)
            this._modules.menuItems.ui.init(true);
    }

    /**
     * Renders and initializes the order summary component.
     */
    private _renderOrderSummary(): void {
        this._modules.summary.init();
    }

    /**
     * Renders the upsell section with available upsell categories.
     * Hides the section if no upsell items are found.
     */
    private _renderUpsellSection(): void {
        const parentDiv = document.getElementById("div_cart_upsell_items");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";
        parentDiv.appendChild(this._modules.upsell.element);
        this._modules.upsell.init(this._data.getProperty("Categories"), this._order.getProperty("Items"));

        const action = this._modules.upsell.hasUpsell() ? Util.showElement : Util.hideElement;
        action("div_cart_upsell");
    }

    /**
     * Renders the coupon section visibility based on settings.
     */
    private readonly _renderCouponSection = (): void => {
        const showCouponCodeInput = this._data.getProperty("Settings").OOS.showCouponCodeBox;

        const action = showCouponCodeInput ? Util.showElement : Util.hideElement;
        action("div_cart_promo_code");


        // 2DO: Refactor in future?
        // if (aOLO.data.Settings.CPNBTN == Setting81.DESKTOP_AND_APP ||
        //     (aOLO.data.Settings.CPNBTN == Setting81.DESKTOP_ONLY && !Util.isAppView()) ||
        //     (aOLO.data.Settings.CPNBTN == Setting81.APP_ONLY && Util.isAppView()))
        //     Util.showElement("div_cart_coupons_list");
        // else
        //     Util.hideElement("div_cart_coupons_list");
    }

    /**
    * Renders the fundraiser section in the cart.
    * Shows available fundraisers and the user's selected fundraiser (if applicable).
    * @private
    */
    private _renderFundraiserSection(): void {
        this._hideFundraiserErrorElement();
        Util.setElement("value", "txt_cart_fundraiser", "");

        const businessDate = Util.getBusinessDate(this._data);
        const fundraisers = this._data.getProperty("Fundraisers").filter(x => new Date(x.StartDateTime) <= businessDate && new Date(x.EndDateTime) >= businessDate);
        if (fundraisers.length > 0) {
            Util.showElement("div_cart_fundraiser");
        } else
            Util.hideElement("div_cart_fundraiser");

        const orderFundraiser = this._order.getProperty("Fundraiser");
        if (orderFundraiser) {
            const fundraiser = this._data.getProperty("Fundraisers").find(x => x.FundraiserId === orderFundraiser.ID);

            if (fundraiser) {
                const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
                    const tempText = this._localizationService.names("PARTICIPATING_IN_FUNDRAISER", lang).replace("{{fundraiser_name}}", fundraiser.Name);
                    return { text: tempText, lc: lang };
                });

                Util.setElement("innerText", "spn_cart_fundraiser_selection", this._localizationService.names("PARTICIPATING_IN_FUNDRAISER").replace("{{fundraiser_name}}", fundraiser.Name));
                Util.setElement("ltagj", "spn_cart_fundraiser_selection", Util.toLtagj(nameObj));
            }

            Util.showElement("div_cart_fundraiser_selection");
        } else {
            Util.hideElement("div_cart_fundraiser_selection");
        }
    }

    /**
     * Handles the removal of the current challenge from the order.
     * Triggers the order-level challenge removal logic.
     * @private
     */
    private readonly _handleChallengeRemove = (): void => {
        this._order.removeChallenge();
    }

    /**
     * Renders the currently active challenge and its structure (members, events, sub-members, and extras) into the cart UI.
     * If no valid challenge is found in the order, it clears the section.
     * @private
     */
    private _renderChallengesSection(): void {
        const orderChallenges = this._order.getProperty("Challenges");
        if (orderChallenges.length === 0) {
            this._clearChallengeSection();
            return;
        }

        const orderChallenge = orderChallenges[0];
        const challenge = this._data.getProperty("Challenges").find(x => x.ChallengeId === orderChallenge.challengeId);
        if (!challenge) {
            this._clearChallengeSection();
            return;
        }

        this._renderChallengeHeader(challenge);
        this._renderChallengeMembers(orderChallenges[0], challenge);
        Util.showElement('div_cart_order_challenges');
    }

    /**
     * Renders the challenge header text and localization for the selected challenge.
     * 
     * @private
     * @param {IDataChallenge} challenge - The selected challenge to render.
     */
    private _renderChallengeHeader(challenge: IDataChallenge): void {
        const challengeName = this._localizationService.GetNewName(challenge.Names);
        const text = this._localizationService.names("CHALLENGE_PARTICIPATION_CONFIRMATION").replace("{{challenge_name}}", challengeName);

        const localized: INewName[] = this._localizationService.getSupportedLanguages().map(lang => ({
            lc: lang,
            text: this._localizationService.names("CHALLENGE_PARTICIPATION_CONFIRMATION", lang).replace("{{challenge_name}}", this._localizationService.GetNewName(challenge.Names, lang)),
        }));

        Util.setElement("innerHtml", "spn_cart_order_challenges", text);
        Util.setElement("ltagj", "spn_cart_order_challenges", Util.toLtagj(localized));
    }

    /**
     * Renders the list of members and their associated events for the current order challenge.
     * 
     * @private
     * @param {IOrderChallenge} orderChallenge - The order's active challenge data.
     * @param {IDataChallenge} challenge - The full challenge definition containing member and event metadata.
     */
    private _renderChallengeMembers(orderChallenge: IOrderChallenge, challenge: IDataChallenge): void {
        let html = "";

        for (const member of orderChallenge.members) {
            const memberData = challenge.Members.find(x => x.MemberId === member.memberId);
            if (!memberData)
                continue;

            html += `<div style="margin-left: 1rem">${memberData.Name}</div>`;
            html += this._renderChallengeEvents(member.events, memberData);
        }

        Util.setElement("innerHtml", "div_cart_order_challenges_members", html);
    }

    /**
     * Generates the nested HTML content for a member's event participation.
     * Handles unique event names, sub-members, and optional extras.
     * 
     * @private
     * @param {IOrderChallengeEvent[]} events - Events tied to the challenge member.
     * @param {IChallengeMember} memberData - Metadata for the challenge member (name and events).
     * @returns {string} - The resulting HTML string to append to the member section.
     */
    private _renderChallengeEvents(events: IOrderChallengeEvent[], memberData: IDataChallengeMember): string {
        const seenEvents = new Set<number>();
        let html = "";

        for (const event of events) {
            const eventData = memberData.Events.find(e => e.EventId === event.eventId);
            if (!eventData)
                continue;

            const subMember = event.subMember ? `<div style="margin-left: 3rem">${event.subMember}</div>` : "";
            const extras = event.extra
                ? event.extra.split(",").map(e => `<div style="margin-left: 4rem">${e}</div>`).join("")
                : "";

            if (!seenEvents.has(event.eventId)) {
                html += `<div style="margin-left: 2rem">${eventData.EventName}</div>` + subMember + extras;
                seenEvents.add(event.eventId);
            } else {
                html += subMember + extras;
            }
        }

        return html;
    }

    /**
     * Clears and hides the UI section for challenge participation when no active challenge exists.
     * @private
     */
    private _clearChallengeSection(): void {
        Util.setElement('innerHtml', 'div_cart_order_challenges_members', "");
        Util.hideElement('div_cart_order_challenges');
    }

    /**
     * Renders the active challenge banner in the cart if there is a valid challenge for the current business date.
     * Skips rendering if the order is for a future date.
     * @private
     */
    private _renderChallengesButton(): void {
        const orderBusinessDate = this._order.getProperty("BUSDate");
        const businessDate = Util.getBusinessDate(this._data);

        Util.hideElement("div_cart_challenges");

        // Skip if order is for a future date that differs from today's business date
        if (this._order.getProperty("FutureDate") != null
            && orderBusinessDate != null
            && !this._isSameDay(orderBusinessDate, businessDate)) {
            return;
        }

        const activeChallenge = this._getActiveChallenge(businessDate);
        if (!activeChallenge)
            return;

        this._displayChallengeButton(activeChallenge);
    }

    /**
     * Compares two dates and returns true if they fall on the same calendar day.
     * @param {string | Date} date1 - First date.
     * @param {string | Date} date2 - Second date.
     * @returns {boolean} True if dates are the same day, false otherwise.
     * @private
     */
    private _isSameDay(date1: string | Date, date2: string | Date): boolean {
        const d1 = new Date(date1);
        const d2 = new Date(date2);
        return d1.setHours(0, 0, 0, 0) === d2.setHours(0, 0, 0, 0);
    }

    /**
     * Retrieves the first active challenge for the given business date.
     * @param {Date} businessDate - Current business date.
     * @returns {IDataChallenge | undefined} The active challenge if any.
     * @private
     */
    private _getActiveChallenge(businessDate: Date): IDataChallenge | undefined {
        return this._data.getProperty("Challenges").find(challenge =>
            [-1, 0].includes(Util.compareDates(new Date(challenge.StartDate), businessDate)) &&
            [0, 1].includes(Util.compareDates(new Date(challenge.EndDate), businessDate))
        );
    }

    /**
     * Displays the challenge banner and binds the modal open event.
     * @param {IDataChallenge} challenge - The challenge to display.
     * @private
     */
    private _displayChallengeButton(challenge: IDataChallenge): void {
        Util.showElement("div_cart_challenges");

        const span = document.getElementById("spn_cart_challenges");
        if (span) {
            span.innerText = this._localizationService.GetNewName(challenge.Names);
            span.setAttribute("ltagj", Util.toLtagj(challenge.Names));
        }

        Util.setElement("onclick", "btn_cart_challenges", () => {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CHALLENGES, { challenge });
        });
    }

    /**
     * Renders and initializes the loyalty rewards section in the cart.
     */
    private _renderLoyaltySection(): void {
        const parentContainer = document.getElementById("div_cart_loyalty");
        if (!parentContainer)
            return;

        parentContainer.innerHTML = "";

        this._modules.cartRewards.init();
        parentContainer.appendChild(this._modules.cartRewards.element);
    }

    /**
     * Hides error elements related to the promo code input.
     */
    private _hideErrorElements(): void {
        Util.setElementClass("remove", "div_cart_coupon_code_input", "invalid-input");
        Util.hideElement("spn_promo_code_error");
    }

    /**
     * Sets an error message for an invalid coupon.
     * @param {string} error - The error message to display.
     */
    private readonly _setInvalidCouponError = (error: string): void => {
        const friendlyError = this._localizationService.names(error);
        Util.setElementClass("add", "div_cart_coupon_code_input", "invalid-input");
        Util.setElement("innerText", "spn_promo_code_error", friendlyError);
        Util.setElement("ltag", "spn_promo_code_error", error);
        Util.showElement("spn_promo_code_error");

        Util.resetAnnounceCycle();
        Util.announce(friendlyError);

        if (this._dataLayer) {
            this._dataLayer.error_message(friendlyError, DataLayerErrorType.USER_ERROR);
        }
    }

    /**
     * Validates the promo code input before applying it.
     * @returns {boolean} - `true` if validation passes, otherwise `false`.
     */
    private _validatePromoCode(): boolean {
        this._hideErrorElements();
        Util.resetAnnounceCycle();
        const inputValue = Util.getElementValue("txt_cart_coupon_code").trim().toLowerCase();

        if (inputValue == "") {
            this._setInvalidCouponError("InvalidCouponRequired");
            return false;
        }

        if (!this._order.isCouponAllowedInFundraiser(false)) {
            this._setInvalidCouponError("REMOVE_FUNDRAISER_FOR_PROMO_CODE");
            return false;
        }

        //Bug #67932 - Only allow unique campaign promotion codes, but regular coupon codes are allowed for stacking purposes
        const orderCoupon = this._order.getProperty("Coupons").find(x => x.CouponCode === inputValue);
        if (Coupon.IsCampaignOrPromoCode(orderCoupon)) {
            this._setInvalidCouponError("InvalidCouponRequired");
            return false;
        }

        return true;
    }

    /**
     * Handles applying a promo code to the cart.
     */
    private readonly _handleApplyPromoCodeAsync = async (): Promise<void> => {
        const validated = this._validatePromoCode();
        if (!validated)
            return;

        const couponCode = Util.getElementValue("txt_cart_coupon_code");
        const campaignRegex = /^c-[a-z0-9]+-[a-z0-9]+-[a-z0-9]+$/;
        const isCampaign = campaignRegex.test(couponCode.toLowerCase());
        await this._order.applyCouponByCodeAsync(this._loyaltyProvider, couponCode, false, false, isCampaign);
        this._renderOrderSummary();
    }

    /**
     * Handles the application of a fundraiser by code input.
     * Validates and triggers the fundraiser modal if valid.
     * @private
     */
    private readonly _handleApplyFundraiserByCode = (): void => {
        const fundraiserCode = Util.getElementValue("txt_cart_fundraiser").trim();
        const fundraiser = this._getFundraiserByCode(fundraiserCode);

        if (!this._validateFundraiserCode(fundraiserCode, fundraiser))
            return;

        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.FUNDRAISER, { fundraiser: fundraiser });
    }

    /**
     * Hides error messages related to fundraiser input.
     * @private
     */
    private _hideFundraiserErrorElement(): void {
        Util.setElementClass("remove", "div_cart_fundraiser_input", "invalid-input");
        Util.hideElement("spn_cart_fundraiser_error");
    }

    /**
     * Displays an error message for an invalid fundraiser.
     * @param {string} error - The error message key.
     * @private
     */
    private _showFundraiserError(error: string): void {
        const friendlyError = this._localizationService.names(error);
        Util.setElementClass("add", "div_cart_fundraiser_input", "invalid-input");
        Util.setElement("innerText", "spn_cart_fundraiser_error", friendlyError);
        Util.setElement("ltag", "spn_cart_fundraiser_error", error);
        Util.showElement("spn_cart_fundraiser_error");

        Util.resetAnnounceCycle();
        Util.announce(friendlyError);

        if (this._dataLayer) {
            this._dataLayer.error_message(friendlyError, DataLayerErrorType.USER_ERROR);
        }
    }

    /**
     * Validates the fundraiser code entered by the user.
     * @param {string} fundraiserCode - The fundraiser code entered.
     * @param {IDataFundraiser | null} fundraiser - The fundraiser object if found.
     * @returns {boolean} - Returns true if the fundraiser is valid, otherwise false.
     * @private
     */
    private _validateFundraiserCode(fundraiserCode: string, fundraiser: IDataFundraiser | null): boolean {
        this._hideFundraiserErrorElement();
        if (fundraiserCode === "") {
            this._showFundraiserError("ENTER_FUNDRAISER_CODE");
            return false;
        }

        if (!fundraiser) {
            this._showFundraiserError("INVALID_OR_EXPIRED_FUNDRAISER_CODE");
            return false;
        }

        if (!fundraiser.IsCouponAllowed && this._order.getProperty("Coupons").length > 0) {
            this._showFundraiserError("FUNDRAISER_CODE_ERROR_COUPON");
            return false;
        }

        return true;
    }

    /**
     * Retrieves a fundraiser by its code if valid and active.
     * @param {string} fundraiserCode - The code to search for.
     * @returns {IDataFundraiser | null} - The matching fundraiser or null if not found.
     * @private
     */
    private _getFundraiserByCode(fundraiserCode: string): IDataFundraiser | null {
        const businessDate = Util.getBusinessDate(this._data);
        const needle = (fundraiserCode ?? "").trim().toUpperCase();
        if (!needle) return null;
        return this._data.getProperty("Fundraisers")
            .find(x => (x.Code ?? "").trim().toUpperCase() === needle && new Date(x.EndDateTime) >= businessDate && !!x.IsOpen) || null;
    }

    /**
     * Removes the selected fundraiser from the order.
     * @private
     */
    private _handleFundraiserRemove = (): void => {
        this._order.removeFundraiser();
        Util.hideElement("div_cart_fundraiser_selection");
    }

    private readonly _onLoggedIn = (): void => {
        this._updateLoyaltyPointsText();
    }

    private readonly _onLoggedOut = (): void => {
        this._updateLoyaltyPointsText();
    }

    private _updateLoyaltyPointsText(): void {
        const diamondIconUrl = `${this._context.getProperty("publicStorageUrl")}/online3/svg/diamond.svg`;
        Util.setElement("src", "img_cart_loyalty_points_icon", diamondIconUrl);

        if (this._loyaltyProvider.hasPointRedemption()) {
            if (this._profileService.user.isLoggedIn()) {
                this._updateLoyaltyPointsTextWhenLoggedIn();
            } else {
                this._updateLoyaltyPointsTextWhenLoggedOut();
            }
            Util.showElement("div_cart_loyalty_points_container");
        } else {
            Util.hideElement("div_cart_loyalty_points_container");
        }
    }

    private readonly _updateLoyaltyPointsTextWhenLoggedIn = (): void => {
        const pointsUnitText = this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo());
        const loyaltyPoints = this._loyaltyProvider.getUserAvailableLoyaltyPoints();

        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map((lang) => {
            const tempText = this._localizationService
                .names("AVAILABLE_POINTS_LABEL_WITH_POINTS", lang)
                .replaceAll("{{points}}", loyaltyPoints.toLocaleString(lang))
                .replaceAll("{{points_unit}}", pointsUnitText);
            return { text: tempText, lc: lang };
        });

        const availablePointsText = this._localizationService
            .names("AVAILABLE_POINTS_LABEL_WITH_POINTS")
            .replaceAll("{{points}}", loyaltyPoints.toLocaleString(this._context.getProperty("languageCode")))
            .replaceAll("{{points_unit}}", pointsUnitText)

        Util.setElement("innerText", "spn_cart_loyalty_available_points", availablePointsText);
        Util.setElement("ltagj", "spn_cart_loyalty_available_points", Util.toLtagj(nameObj));
        Util.showElement("spn_cart_loyalty_available_points");
        Util.hideElement("a_cart_loyalty_signin_prompt");
    }

    private readonly _updateLoyaltyPointsTextWhenLoggedOut = (): void => {
        Util.hideElement("spn_cart_loyalty_available_points");
        Util.showElement("a_cart_loyalty_signin_prompt");
    }

    private readonly _showSignInModal = (): void => {
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
    }

    private _setKeyboardEventForLoyaltySignInPrompt(): void {
        const loyalTySignInPrompt = document.getElementById("a_cart_loyalty_signin_prompt");
        if (loyalTySignInPrompt)
            Util.addKeyboardActivation(loyalTySignInPrompt, () => this._showSignInModal());
    }

    // 2DO: Refactor? Delete?
    // private GUI_SetOrder_Get_Item_Discount = (oItem: IOrderItem): string | null => {
    //     const coupon = this._order.getProperty("ItemsCoupons").find(icpn => icpn.ItemKey === oItem.ItemKey);
    //     if (!!coupon)
    //         return (this.GUI_SetOrder_GetOrderCouponIDByCouponKey(coupon.CouponKey) === CouponIdList.LOYALTY_REDEEM_ITEM) ? "Redeemed" : "Discounted";

    //     return null;
    // }

    // private GUI_SetOrder_GetOrderCouponIDByCouponKey = (key: number): number | null => {
    //     const coupon = this._order.getProperty("Coupons").find(cpn => cpn.CouponKey === key);
    //     return coupon ? coupon.CouponId : null;
    // }

    // 2DO: When Local Coupons Section Added
    // private _GUI_DisplayCoupons = (): void => {
    //     const fundraiser = aOLO.data.Fundraisers.find(fund => fund.FundraiserId == aOLO.Order.Fundraiser?.ID);
    //     if (fundraiser && !fundraiser.IsCouponAllowed) {
    //         let msg = Names("CouponCodeErrorWithFundraiser");
    //         DialogCreators.messageBoxOk(msg.replace("??", fundraiser.Name || ""), aOLO.buttonHoverStyle);
    //         return;
    //     }

    //     Util.hideElement("div_items");
    //     Util.showElement("div_coupons");

    //     OnlineOrderingUtil.ToggleOrderItems(true, aOLO);
    //     OnlineOrderingUtil.GUI_ScrollTo(0, aOLO.Temp.HeaderHeight);
    //     aOLO.floatSidebar.forceUpdate();

    //     if (aOLO.Modules.DataLayer)
    //         aOLO.Modules.DataLayer.couponScrollListeners();
    // }
}